import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";
import {
  getConversationHistory,
  sendChatMessage,
} from "../../services/chatApi";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";

const THEME_ORANGE = "#F07828";
const BG = "#F7F7F7";

const ChatWindow = ({ conversationId, onNewChat }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async (id = conversationId) => {
    if (!id) return;

    try {
      const res = await getConversationHistory(id);
      setMessages(res.messages || []);
    } catch (err) {
      console.error("Failed to load chat history:", err);
      setMessages([]);
    }
  };

  useEffect(() => {
    setMessages([]);
    if (conversationId) {
      loadHistory(conversationId);
    }
  }, [conversationId]);

  const handleSend = async (text) => {
    if (!text?.trim() || !conversationId) return;

    setLoading(true);

    try {
      const optimisticUserMsg = {
        MessageId: `temp-${Date.now()}`,
        SenderType: "USER",
        MessageText: text,
      };

      setMessages((prev) => [...prev, optimisticUserMsg]);

      await sendChatMessage({
        conversationId,
        message: text,
      });

      await loadHistory(conversationId);
    } catch (err) {
      console.error("Failed to send chat message:", err);
      await loadHistory(conversationId);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChatClick = async () => {
    if (loading) return;

    try {
      setMessages([]);
      if (typeof onNewChat === "function") {
        await onNewChat();
      }
    } catch (err) {
      console.error("Failed to start new chat:", err);
    }
  };

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          bgcolor: THEME_ORANGE,
          color: "#fff",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography fontWeight={700}>UniFLOO Assistant</Typography>
          <Typography variant="caption">
            Ask about attendance, fees, marks, and more
          </Typography>
        </Box>

        <Tooltip title="Start new chat">
          <span>
            <IconButton
              size="small"
              onClick={handleNewChatClick}
              disabled={loading}
              sx={{
                color: "#fff",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, bgcolor: BG, overflow: "hidden" }}>
        <MessageList messages={messages} />
      </Box>

      <ChatInput onSend={handleSend} loading={loading} />
    </>
  );
};

export default ChatWindow;